import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useTestClock } from "@/contexts/TestClockContext";
import { safeLog } from '@/utils/safeLogger';

export type CreditWarningLevel = 'none' | 'low' | 'critical' | 'depleted';

export interface CreditStatistics {
  totalUsed: number;
  usedLast7Days: number;
  usedThisPeriod: number;
  currentBalance: number | null;
}

export interface CreditSystemState {
  // Current balance
  credits: number | null;
  
  // Usage statistics
  stats: CreditStatistics;
  
  // Subscription info
  hasSubscription: boolean;
  tier: string | null;
  creditsMonthly: number | null;
  
  // Warning level
  warningLevel: CreditWarningLevel;
  
  // Loading state
  isLoading: boolean;
  
  // Refresh function
  refresh: () => Promise<void>;
}

/**
 * Unified Credit System Hook
 * Replaces: useCreditStatistics, useAddinCreditStatus
 * 
 * Features:
 * - Single realtime listener (prevents RC-3)
 * - Optimistic updates (reduces RC-4)
 * - Lazy stats loading (only when needed)
 * - Centralized warning level calculation
 */
export function useCreditSystem(): CreditSystemState {
  const { getCurrentTime } = useTestClock();
  
  const [credits, setCredits] = useState<number | null>(null);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [tier, setTier] = useState<string | null>(null);
  const [creditsMonthly, setCreditsMonthly] = useState<number | null>(null);
  const [currentPeriodStart, setCurrentPeriodStart] = useState<string | null>(null);
  
  const [stats, setStats] = useState<CreditStatistics>({
    totalUsed: 0,
    usedLast7Days: 0,
    usedThisPeriod: 0,
    currentBalance: null,
  });
  
  const [isLoading, setIsLoading] = useState(true);

  // Calculate warning level based on current credits
  const getWarningLevel = useCallback((currentCredits: number | null, hasSub: boolean): CreditWarningLevel => {
    if (!hasSub) return 'depleted';
    if (currentCredits === null) return 'none';
    if (currentCredits === 0) return 'depleted';
    if (currentCredits <= 5) return 'critical';
    if (currentCredits <= 10) return 'low';
    return 'none';
  }, []);

  // Load subscription and credits
  const loadCredits = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('[useCreditSystem] No user found');
        setIsLoading(false);
        return;
      }

      console.log('[useCreditSystem] Loading credits for user:', user.id);

      // ✅ SINGLE query instead of multiple
      const { data: subscription, error } = await supabase
        .from('subscriptions')
        .select('credits_remaining, tier, credits_monthly, current_period_start, status')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('[useCreditSystem] Error loading subscription:', error);
        setHasSubscription(false);
        setIsLoading(false);
        return;
      }

      if (!subscription) {
        console.log('[useCreditSystem] No subscription found');
        setHasSubscription(false);
        setCredits(null);
        setTier(null);
        setCreditsMonthly(null);
        setIsLoading(false);
        return;
      }

      console.log('[useCreditSystem] Subscription loaded:', {
        credits: subscription.credits_remaining,
        tier: subscription.tier,
        status: subscription.status
      });

      setHasSubscription(true);
      setCredits(subscription.credits_remaining);
      setTier(subscription.tier);
      setCreditsMonthly(subscription.credits_monthly);
      setCurrentPeriodStart(subscription.current_period_start);
      setIsLoading(false);

    } catch (error) {
      console.error('[useCreditSystem] Error loading credits:', error);
      setIsLoading(false);
    }
  }, []);

  // Load usage statistics (lazy - only when needed)
  const loadStatistics = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !currentPeriodStart) return;

      console.log('[useCreditSystem] Loading statistics');

      // Parallel queries for better performance
      const [allTransactions, last7DaysTransactions, currentPeriodTransactions] = await Promise.all([
        // Total used (all time)
        supabase
          .from('credit_transactions')
          .select('final_credits')
          .eq('user_id', user.id)
          .neq('action_type', 'topup')
          .neq('action_type', 'subscription')
          .neq('action_type', 'subscription_renewal')
          .neq('action_type', 'subscription_purchase'),
        
        // Last 7 days
        supabase
          .from('credit_transactions')
          .select('final_credits')
          .eq('user_id', user.id)
          .gte('created_at', new Date(getCurrentTime().getTime() - 7 * 24 * 60 * 60 * 1000).toISOString())
          .gt('final_credits', 0)
          .neq('action_type', 'topup')
          .neq('action_type', 'subscription')
          .neq('action_type', 'subscription_renewal')
          .neq('action_type', 'subscription_purchase'),
        
        // Current billing period
        supabase
          .from('credit_transactions')
          .select('final_credits')
          .eq('user_id', user.id)
          .gte('created_at', currentPeriodStart)
          .gt('final_credits', 0)
          .neq('action_type', 'topup')
          .neq('action_type', 'subscription')
          .neq('action_type', 'subscription_renewal')
          .neq('action_type', 'subscription_purchase')
      ]);

      const totalUsed = (allTransactions.data || []).reduce((sum, t) => sum + t.final_credits, 0);
      const usedLast7Days = (last7DaysTransactions.data || []).reduce((sum, t) => sum + t.final_credits, 0);
      const usedThisPeriod = (currentPeriodTransactions.data || []).reduce((sum, t) => sum + t.final_credits, 0);

      setStats({
        totalUsed,
        usedLast7Days,
        usedThisPeriod,
        currentBalance: credits,
      });

    } catch (error) {
      console.error('[useCreditSystem] Error loading statistics:', error);
    }
  }, [currentPeriodStart, getCurrentTime]);

  // Initial load
  useEffect(() => {
    loadCredits();
  }, [loadCredits]);

  // Load stats when period start is available
  useEffect(() => {
    if (currentPeriodStart && hasSubscription) {
      loadStatistics();
    }
  }, [currentPeriodStart, hasSubscription, loadStatistics]);

  // ✅ SINGLE realtime listener (prevents RC-3: Multiple Realtime Listeners)
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (!data.user) return;

      const channel = supabase
        .channel('unified-credit-updates')
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'subscriptions',
            filter: `user_id=eq.${data.user.id}`
          },
          (payload) => {
            console.log('[useCreditSystem] Subscription updated via realtime:', payload);
            
            // ✅ Optimistic update (reduces RC-4: Frontend-Backend Sync Gap)
            if (payload.new && 'credits_remaining' in payload.new) {
              setCredits(payload.new.credits_remaining as number);
              setTier(payload.new.tier as string);
              setCreditsMonthly(payload.new.credits_monthly as number);
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'credit_transactions',
            filter: `user_id=eq.${data.user.id}`
          },
          (payload) => {
            console.log('[useCreditSystem] New transaction detected, refreshing stats');
            // Reload stats after a short delay
            setTimeout(() => loadStatistics(), 300);
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    });
  }, [loadStatistics]);

  const warningLevel = getWarningLevel(credits, hasSubscription);

  return {
    credits,
    stats,
    hasSubscription,
    tier,
    creditsMonthly,
    warningLevel,
    isLoading,
    refresh: loadCredits,
  };
}
